import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import axios from "axios";
import DatePickerModal from "../../components/DatePickerModal/DatePickerModal";
import Lf23Loader from "../../components/Lf23Loader";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
const BASE = `${API_URL}/report-sap-capisquadra`;
// Le chiamate che assegnano scrivono su Interacta: il backend vuole login + permesso.
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
const selectMenuPortalStyles = { menuPortal: (base) => ({ ...base, zIndex: 10000 }) };

// Default del periodo: ultimi 30 giorni, oggi compreso (date locali, non UTC).
const isoLocale = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const oggi = () => isoLocale(new Date());
const giorniFa = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return isoLocale(d); };

const formatItalianFromIso = (iso) => {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
};

const toTitleCase = (s) => {
    if (s == null) return s;
    return String(s).toLowerCase().replace(/(?<=^|[^\p{L}])\p{L}/gu, (c) => c.toUpperCase());
};

// Tab "Non assegnati" (task 500): i servizi che la DAG airflow Trans_capisquadra_non_assegnato
// ha spostato in "Non assegnato" perché nessun caposquadra li ha presi in carico entro 30
// minuti dall'ora di fine. Raggruppati per community (una per tipologia di caposquadra),
// stesso drilldown della tab Report. Da qui si possono assegnare a un caposquadra: il backend
// esegue su Interacta la "Presa in carico tardiva" con l'Assegnatario scelto (-> In servizio).
const TabNonAssegnati = () => {
    const [dal, setDal] = useState(() => giorniFa(29));
    const [al, setAl] = useState(oggi);
    const [dati, setDati] = useState(null);           // { dal, al, totale, communities: [...] }
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [aperte, setAperte] = useState({});          // community id -> aperta (default: aperta)
    const [expanded, setExpanded] = useState(() => new Set()); // postId con il dettaglio aperto

    // Assegnazione
    const [selezionati, setSelezionati] = useState(() => new Map()); // postId -> servizio (con communityId)
    const [modale, setModale] = useState(null);        // { servizi: [...] } quando si sta assegnando
    const [assegnatari, setAssegnatari] = useState([]); // [{ id, nome }] gruppo Capisquadra Ambiente
    const [scelto, setScelto] = useState(null);         // opzione react-select
    const [assegnando, setAssegnando] = useState(false);
    const [erroreModale, setErroreModale] = useState(null);
    const [esito, setEsito] = useState(null);           // { tipo: "ok"|"warn", testo }

    // Eliminazione (task 501): pulizia dei doppioni rimasti in "Non assegnato"
    const [elimina, setElimina] = useState(null);       // { servizi: [...] } quando si sta eliminando
    const [eliminando, setEliminando] = useState(false);
    const [erroreElimina, setErroreElimina] = useState(null);

    const carica = useCallback(async (d, a) => {
        if (!d || !a) return;
        if (a < d) { setError("La data finale precede quella iniziale."); return; }
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(`${BASE}/non-assegnati`, { params: { dal: d, al: a } });
            setDati(res.data);
            setAperte({});
            setExpanded(new Set());
            setSelezionati(new Map());
        } catch (err) {
            console.error("Errore non-assegnati:", err);
            setError(err.response?.data?.error || "Errore nel recupero dei servizi non assegnati.");
            setDati(null);
        } finally {
            setLoading(false);
        }
    }, []);

    // Primo caricamento con il periodo di default; poi si ricarica con "Aggiorna".
    useEffect(() => { carica(giorniFa(29), oggi()); }, [carica]);

    const isAperta = (c) => (aperte[c.id] !== undefined ? aperte[c.id] : true);
    const toggleCommunity = (c) => setAperte((prev) => ({ ...prev, [c.id]: !isAperta(c) }));
    const toggleExpanded = (postId) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(postId)) next.delete(postId); else next.add(postId);
        return next;
    });

    const conCommunity = (c, r) => ({ ...r, communityId: c.id, community: c.name });
    const toggleSelezione = (c, r) => setSelezionati((prev) => {
        const next = new Map(prev);
        if (next.has(r.postId)) next.delete(r.postId); else next.set(r.postId, conCommunity(c, r));
        return next;
    });
    const tuttiSelezionati = (c) => c.rows.length > 0 && c.rows.every((r) => selezionati.has(r.postId));
    const toggleTuttiCommunity = (c) => setSelezionati((prev) => {
        const next = new Map(prev);
        if (c.rows.every((r) => next.has(r.postId))) c.rows.forEach((r) => next.delete(r.postId));
        else c.rows.forEach((r) => next.set(r.postId, conCommunity(c, r)));
        return next;
    });

    // Apre il modale; l'elenco dei capisquadra si carica la prima volta che serve.
    const apriAssegna = async (servizi) => {
        setErroreModale(null);
        setScelto(null);
        setModale({ servizi });
        if (assegnatari.length) return;
        try {
            const res = await axios.get(`${BASE}/assegnatari`, { headers: authHeaders() });
            setAssegnatari(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Errore assegnatari:", err);
            setErroreModale(err.response?.data?.error || "Errore nel recupero dei capisquadra.");
        }
    };

    const confermaAssegna = async () => {
        if (!modale || !scelto || assegnando) return;
        setAssegnando(true);
        setErroreModale(null);
        try {
            const res = await axios.post(`${BASE}/non-assegnati/assegna`, {
                assegnatarioId: scelto.value,
                assegnazioni: modale.servizi.map((x) => ({ postId: x.postId, communityId: x.communityId })),
            }, { headers: authHeaders() });
            const { assegnati = 0, giaPresi = 0, errori = [] } = res.data || {};
            const parti = [`${assegnati} ${assegnati === 1 ? "servizio assegnato" : "servizi assegnati"} a ${scelto.label}`];
            if (giaPresi) parti.push(`${giaPresi} già ${giaPresi === 1 ? "preso" : "presi"} in carico nel frattempo`);
            if (errori.length) parti.push(`${errori.length} non ${errori.length === 1 ? "assegnato" : "assegnati"}: ${[...new Set(errori.map((e) => e.error))].join(" · ")}`);
            setEsito({ tipo: errori.length ? "warn" : "ok", testo: parti.join(" · ") });
            setModale(null);
            if (dati) await carica(dati.dal, dati.al);
        } catch (err) {
            console.error("Errore assegnazione:", err);
            setErroreModale(err.response?.data?.error || "Errore nell'assegnazione.");
        } finally {
            setAssegnando(false);
        }
    };

    const confermaElimina = async () => {
        if (!elimina || eliminando) return;
        setEliminando(true);
        setErroreElimina(null);
        try {
            const res = await axios.post(`${BASE}/non-assegnati/elimina`, {
                servizi: elimina.servizi.map((x) => ({ postId: x.postId, communityId: x.communityId, doppioneDi: x.doppioneDi?.postId ?? null })),
            }, { headers: authHeaders() });
            const { eliminati = 0, giaPresi = 0, errori = [] } = res.data || {};
            const parti = [`${eliminati} ${eliminati === 1 ? "servizio eliminato" : "servizi eliminati"} da Interacta`];
            if (giaPresi) parti.push(`${giaPresi} non ${giaPresi === 1 ? "eliminato" : "eliminati"}: presi in carico nel frattempo`);
            if (errori.length) parti.push(`${errori.length} con errore: ${[...new Set(errori.map((e) => e.error))].join(" · ")}`);
            setEsito({ tipo: errori.length ? "warn" : "ok", testo: parti.join(" · ") });
            setElimina(null);
            if (dati) await carica(dati.dal, dati.al);
        } catch (err) {
            console.error("Errore eliminazione:", err);
            setErroreElimina(err.response?.data?.error || "Errore nell'eliminazione.");
        } finally {
            setEliminando(false);
        }
    };
    const apriElimina = (servizi) => { setErroreElimina(null); setElimina({ servizi }); };

    // Solo le community che hanno servizi non assegnati: le altre non dicono niente.
    // Il totale delle community resta nel riepilogo ("X di 11").
    const tutte = dati?.communities || [];
    const communities = tutte.filter((c) => c.totale > 0);
    // Una community che non si è potuta leggere va segnalata anche se a zero, altrimenti
    // "nessun servizio" sembrerebbe vero anche per lei.
    const conAvviso = tutte.filter((c) => c.totale === 0 && c.avviso);

    return (
        <>
            <div className="filter-panel">
                <div className="filter-grid report-sap-na-filters">
                    <div className="filter-group">
                        <label>Dal</label>
                        <DatePickerModal value={dal} onChange={(e) => setDal(e.target.value)} max={al || undefined} label="Dal" />
                    </div>
                    <div className="filter-group">
                        <label>Al</label>
                        <DatePickerModal value={al} onChange={(e) => setAl(e.target.value)} min={dal || undefined} label="Al" />
                    </div>
                </div>
                <div className="report-sap-filter-actions report-sap-na-actions">
                    <div className="report-sap-summary">
                        <span className="report-sap-summary-value">{dati ? dati.totale : "—"}</span>
                        <span className="report-sap-summary-label">
                            servizi non assegnati{dati ? ` · ${communities.length} di ${tutte.length} community` : ""}
                        </span>
                    </div>
                    <button type="button" className="report-sap-btn-primary" onClick={() => carica(dal, al)} disabled={loading}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" />
                        </svg>
                        {loading ? "Carico..." : "Aggiorna"}
                    </button>
                </div>
            </div>

            {error && <div className="report-sap-error" role="alert">{error}</div>}
            {conAvviso.length > 0 && (
                <div className="report-sap-error" role="alert">
                    {conAvviso.map((c) => `${c.name}: ${c.avviso}`).join(" · ")}
                </div>
            )}

            {esito && (
                <div className={`report-sap-na-esito report-sap-na-esito--${esito.tipo}`} role="status">
                    <span>{esito.testo}</span>
                    <button type="button" onClick={() => setEsito(null)} aria-label="Chiudi">×</button>
                </div>
            )}

            {selezionati.size > 0 && (
                <div className="report-sap-na-selbar">
                    <span>{selezionati.size === 1 ? "1 servizio selezionato" : `${selezionati.size} servizi selezionati`}</span>
                    <div className="report-sap-na-selbar-actions">
                        <button type="button" className="report-sap-btn-secondary" onClick={() => setSelezionati(new Map())}>Deseleziona</button>
                        <button type="button" className="report-sap-annulla-btn" onClick={() => apriElimina([...selezionati.values()])}>Elimina selezionati</button>
                        <button type="button" className="report-sap-btn-primary" onClick={() => apriAssegna([...selezionati.values()])}>Assegna selezionati</button>
                    </div>
                </div>
            )}

            {loading && !dati && <Lf23Loader testo="Caricamento servizi non assegnati..." />}

            {!loading && dati && dati.totale === 0 && !error && (
                <div className="report-sap-empty">
                    Nessun servizio non assegnato dal {formatItalianFromIso(dati.dal)} al {formatItalianFromIso(dati.al)}.
                </div>
            )}

            {communities.length > 0 && (
            <div className="report-sap-list">
                {communities.map((c) => {
                    const aperta = isAperta(c);
                    return (
                        <section key={c.id} className="report-sap-localita-card">
                            <div className="report-sap-localita-header">
                                <button type="button" className="report-sap-localita-header-toggle" onClick={() => toggleCommunity(c)} aria-expanded={aperta}>
                                    <div className="report-sap-localita-title">
                                        <svg className={`report-sap-chevron ${aperta ? "" : "collapsed"}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="6 9 12 15 18 9" />
                                        </svg>
                                        <div className="report-sap-localita-heading">
                                            <span className="report-sap-localita-name">{c.name}</span>
                                            <span className="report-sap-localita-tipologie">
                                                {c.tipologie.join(" · ")}
                                                {c.avviso && <span className="report-sap-localita-warning"> · ⚠ {c.avviso}</span>}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="report-sap-localita-kpi">
                                        <span className="report-sap-localita-count">
                                            {c.totale === 1 ? "1 servizio non assegnato" : `${c.totale} servizi non assegnati`}
                                        </span>
                                        {c.doppioni > 0 && (
                                            <span className="report-sap-na-doppioni-count" title="Servizi per cui esiste già un altro post uguale: si possono eliminare">
                                                {c.doppioni === 1 ? "1 doppione" : `${c.doppioni} doppioni`}
                                            </span>
                                        )}
                                    </div>
                                </button>
                            </div>

                            {aperta && (
                                <div className="report-sap-table-wrap">
                                    <table className="report-sap-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: "3%" }} className="report-sap-expand-col" aria-label="Dettagli" />
                                                <th style={{ width: "3%" }} className="report-sap-na-check-col">
                                                    <input type="checkbox" checked={tuttiSelezionati(c)} onChange={() => toggleTuttiCommunity(c)} aria-label={`Seleziona tutti i servizi di ${c.name}`} />
                                                </th>
                                                <th style={{ width: "21%" }}>Attività</th>
                                                <th style={{ width: "23%" }}>Albero</th>
                                                <th style={{ width: "10%" }}>Data</th>
                                                <th style={{ width: "11%" }}>Orario</th>
                                                <th style={{ width: "10%" }}>Servizio</th>
                                                <th style={{ width: "19%" }} className="report-sap-action-col">Interacta</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {c.rows.map((r) => {
                                                const isExpanded = expanded.has(r.postId);
                                                return (
                                                    <React.Fragment key={r.postId}>
                                                        <tr>
                                                            <td className="report-sap-expand-col">
                                                                <button type="button" className={`report-sap-expand-btn ${isExpanded ? "open" : ""}`} onClick={() => toggleExpanded(r.postId)} title={isExpanded ? "Comprimi dettagli" : "Mostra dettagli"} aria-expanded={isExpanded}>
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                                                                </button>
                                                            </td>
                                                            <td className="report-sap-na-check-col">
                                                                <input type="checkbox" checked={selezionati.has(r.postId)} onChange={() => toggleSelezione(c, r)} aria-label="Seleziona servizio" />
                                                            </td>
                                                            <td className="report-sap-servizio-cell">
                                                                {r.attivita || "—"}
                                                                {r.doppioneDi && (
                                                                    <span className="report-sap-na-doppione" title={`Esiste già il post ${r.doppioneDi.customId || `#${r.doppioneDi.postId}`}${r.doppioneDi.stato ? ` (${r.doppioneDi.stato})` : ""} per lo stesso servizio`}>
                                                                        Doppione
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td>{r.albero || "—"}</td>
                                                            <td>{formatItalianFromIso(r.data)}</td>
                                                            <td>{r.oraInizio || "—"} – {r.oraFine || "—"}</td>
                                                            <td>{r.servizio || "—"}</td>
                                                            <td className="report-sap-action-col">
                                                                <div className="report-sap-na-azioni">
                                                                <button type="button" className="report-sap-publish-btn" onClick={() => apriAssegna([conCommunity(c, r)])} title="Assegna a un caposquadra (Presa in carico tardiva)">
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></svg>
                                                                    Assegna
                                                                </button>
                                                                <button type="button" className="report-sap-annulla-btn" onClick={() => apriElimina([conCommunity(c, r)])} title="Elimina il post da Interacta (es. doppione)">
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                                                                    Elimina
                                                                </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        {isExpanded && (
                                                            <tr className="report-sap-detail-row">
                                                                <td colSpan={8}>
                                                                    <div className="report-sap-detail-panel">
                                                                        <div className="report-sap-detail-panel-accent" aria-hidden="true" />
                                                                        <div className="report-sap-detail-grid">
                                                                            <div className="report-sap-detail-item"><span className="report-sap-detail-label">Operatori</span><span className="report-sap-detail-value">{r.operatori.length ? r.operatori.map(toTitleCase).join(", ") : "—"}</span></div>
                                                                            <div className="report-sap-detail-item"><span className="report-sap-detail-label">Automezzi</span><span className="report-sap-detail-value report-sap-detail-value--mono">{r.automezzi.length ? r.automezzi.join(", ") : "—"}</span></div>
                                                                            <div className="report-sap-detail-item"><span className="report-sap-detail-label">Post Interacta</span><span className="report-sap-detail-value report-sap-detail-value--mono">{r.customId ? `${r.customId} · ` : ""}#{r.postId}</span></div>
                                                                            {r.doppioneDi && (
                                                                                <div className="report-sap-detail-item"><span className="report-sap-detail-label">Doppione di</span><span className="report-sap-detail-value report-sap-detail-value--mono">{r.doppioneDi.customId ? `${r.doppioneDi.customId} · ` : ""}#{r.doppioneDi.postId}{r.doppioneDi.stato ? ` (${r.doppioneDi.stato})` : ""}</span></div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>
            )}

            {modale && (
                <div className="report-sap-confirm-overlay" onClick={() => { if (!assegnando) setModale(null); }}>
                    <div className="report-sap-confirm-modal report-sap-na-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>{modale.servizi.length === 1 ? "Assegna il servizio" : `Assegna ${modale.servizi.length} servizi`}</h3>
                        <ul className="report-sap-na-modal-list">
                            {modale.servizi.slice(0, 10).map((x) => (
                                <li key={x.postId}>
                                    <strong>{formatItalianFromIso(x.data)}</strong> {x.oraInizio || "—"}–{x.oraFine || "—"} · {x.attivita || "—"}
                                    <span className="report-sap-na-modal-community"> · {x.community}</span>
                                </li>
                            ))}
                            {modale.servizi.length > 10 && <li className="report-sap-na-modal-more">e altri {modale.servizi.length - 10}…</li>}
                        </ul>
                        <label className="report-sap-na-modal-label">Caposquadra</label>
                        <Select
                            placeholder="Cerca il caposquadra..."
                            options={assegnatari.map((a) => ({ value: a.id, label: a.nome }))}
                            value={scelto}
                            onChange={(o) => setScelto(o || null)}
                            isLoading={!assegnatari.length && !erroreModale}
                            classNamePrefix="filter-select"
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            styles={selectMenuPortalStyles}
                            autoFocus
                        />
                        <p className="report-sap-na-modal-note">
                            Su Interacta il servizio passa in <strong>In servizio</strong> con questo Assegnatario (come una "Presa in carico tardiva"),
                            e sul post resta un commento con il tuo nome.
                        </p>
                        {erroreModale && <div className="report-sap-error" role="alert">{erroreModale}</div>}
                        <div className="report-sap-confirm-actions">
                            <button type="button" className="report-sap-btn-secondary" onClick={() => setModale(null)} disabled={assegnando}>Annulla</button>
                            <button type="button" className="report-sap-btn-primary" onClick={confermaAssegna} disabled={!scelto || assegnando}>
                                {assegnando ? "Assegno..." : "Assegna"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {elimina && (() => {
                const nDoppioni = elimina.servizi.filter((x) => x.doppioneDi).length;
                const nAltri = elimina.servizi.length - nDoppioni;
                return (
                    <div className="report-sap-confirm-overlay" onClick={() => { if (!eliminando) setElimina(null); }}>
                        <div className="report-sap-confirm-modal report-sap-na-modal" onClick={(e) => e.stopPropagation()}>
                            <h3>{elimina.servizi.length === 1 ? "Elimina il servizio" : `Elimina ${elimina.servizi.length} servizi`}</h3>
                            <ul className="report-sap-na-modal-list">
                                {elimina.servizi.slice(0, 10).map((x) => (
                                    <li key={x.postId}>
                                        <strong>{formatItalianFromIso(x.data)}</strong> {x.oraInizio || "—"}–{x.oraFine || "—"} · {x.attivita || "—"}
                                        <span className="report-sap-na-modal-community"> · {x.community}</span>
                                        {x.doppioneDi && <span className="report-sap-na-modal-community"> · resta {x.doppioneDi.customId || `#${x.doppioneDi.postId}`}</span>}
                                    </li>
                                ))}
                                {elimina.servizi.length > 10 && <li className="report-sap-na-modal-more">e altri {elimina.servizi.length - 10}…</li>}
                            </ul>
                            <p className="report-sap-na-modal-note">
                                {nDoppioni > 0 && <>{nDoppioni === 1 ? "È un doppione" : `${nDoppioni} sono doppioni`}: il post uguale già esistente resta su Interacta. </>}
                                {nAltri > 0 && <><strong>{nAltri === 1 ? "Un servizio non ha" : `${nAltri} servizi non hanno`} un doppione</strong>: {nAltri === 1 ? "sparirà" : "spariranno"} da Interacta. </>}
                                I post vengono eliminati da Interacta; l'operazione resta nello storico con il tuo nome.
                            </p>
                            {erroreElimina && <div className="report-sap-error" role="alert">{erroreElimina}</div>}
                            <div className="report-sap-confirm-actions">
                                <button type="button" className="report-sap-btn-secondary" onClick={() => setElimina(null)} disabled={eliminando}>Annulla</button>
                                <button type="button" className="report-sap-annulla-btn" onClick={confermaElimina} disabled={eliminando}>
                                    {eliminando ? "Elimino..." : "Elimina"}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </>
    );
};

export default TabNonAssegnati;
